// src/features/payment/hooks/useOrderProcessing.ts
import { useEffect, useState } from "react";
import { useCartData } from "src/business-types/laundry/modules/home/Payment/hooks/useCartData";
import { useCustomerData } from "src/business-types/laundry/modules/home/Payment/hooks/useCustomerData";
import { useCurrentTime } from "src/business-types/laundry/modules/home/Payment/hooks/useCurrentTime";
import { usePayment } from "src/business-types/laundry/modules/home/Payment/hooks/usePayment";
import { errorHandling } from "src/shared/utils/errorHandling";
import { deleteShoppingCart, getOrderDraft, postCreditApprovalRequest, postOrder, putLedgerByCustomer, putOrderDraft } from "src/shared/services/servicesv2";
import { processCardPayment } from "src/paymentGatway/useCardPayment";
import { useUser } from "src/shared/context/UserContext";
import { notifyError, notifySuccess, notifyWarning } from "src/assets/toastService";
import { DraftOrderDetail } from "src/shared/modules/draft/hooks/useDraft";

export const useOrderProcessing = (cartId?: string | null, editedAmounts: { [productId: string]: string } = {}) => {
  const [backToPayment, setBackToPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [showBalance, setShowBalance] = useState(false);
  const [prvDelete, setPrvDelete] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const { cartProducts, loading: cartLoading } = useCartData(cartId);
  const customerId = cartProducts?.[0]?.customerId;
  const { customer } = useCustomerData(customerId);
  const { currentTime } = useCurrentTime();
  const { roundAmount } = usePayment();
  const { userConfigration } = useUser();
  const [overCreditLimit, setOverCreditLimit] = useState(false);
  const [approvalContacts, setApprovalContacts] = useState<{ name: string; email: string; role: string }[]>([]);
  const [draftOrderId, setDraftOrderId] = useState<string | null>(null);
  const [finishProcess, setFinishProcess] = useState<string>("")
  const now = new Date();
  const localDate = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
  const [loading, setLoading] = useState<boolean>(false)
  const [draft, setDraft] = useState<DraftOrderDetail | null>(null);



  const updatedProducts =
    cartProducts[0]?.products?.map((product) => {
      const editedPrice = Number(editedAmounts[product.productId] ?? product.unitPrice);

      return {
        ...product,
        unitPrice: editedPrice,
        itemTotal: editedPrice * Number(product.quantity),
      };
    }) || [];

  useEffect(() => {
    const balance = customer?.totalOutstandingBalance;

    if (balance != null && balance <= 0) {
      setShowBalance(true);
    } else {
      setShowBalance(false);
    }
  }, [customer?.totalOutstandingBalance]);

  useEffect(() => {
    setReceivedAmount(0);
  }, [showBalance]);


  const generateOrderId = () => {
    return `${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}${String(now.getHours()).padStart(2, "0")}${String(
      now.getMinutes(),
    ).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
  };

  const handleOrderSuccess = async (method: "Cash" | "Buy on Credit" | "Card") => {
    setButtonLoading(true);
    const orderAmount = updatedProducts.reduce((sum, product) => sum + Number(product.itemTotal), 0);
    const discount = cartProducts.reduce((sum, item) => sum + parseFloat(item.discount || "0"), 0);
    const deliveryFee = cartProducts.length > 0 ? Number(cartProducts[0].deliveryFee || 0) : 0;

    const finalAmount = orderAmount - discount + deliveryFee;

    const localDate = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
    const newOrderId = generateOrderId();
    setOrderId(newOrderId);

    const out_standing_balance = Number(customer?.totalOutstandingBalance)
    const finalCheck = out_standing_balance < 0 ? finalAmount >= Math.abs(out_standing_balance) ? Math.abs(out_standing_balance) : finalAmount : 0
    const received_amount = out_standing_balance < 0 ? ((receivedAmount ?? 0) + finalCheck) : receivedAmount
    const adjust = out_standing_balance < 0 ? finalCheck : 0

    const order = {
      customerId: cartProducts[0]?.customerId,
      products: updatedProducts,
      orderId: newOrderId,
      orderStatus: "Confirm",
      orderDate: localDate,
      orderTime: now.toTimeString().split(" ")[0],
      orderAmount: orderAmount.toString(),
      discount: discount.toString(),
      finalAmount: finalAmount.toString(),
      outstandingBalance: customer?.totalOutstandingBalance ?? 0,
      cancelOrderAmount: "0",
      deliveryFee: Number(deliveryFee),
      payments: { paymentMethod: method, receivedAmount: method !== "Buy on Credit" ? receivedAmount.toString() : "0", creditAdjust: adjust, },
      paymentHistory: [{ paymentMethod: method, receivedAmount: method !== "Buy on Credit" ? receivedAmount.toString() : "0" }],
      remainingAmount: Math.max(finalAmount - received_amount, 0).toString()
    };

    if (method === "Card" && Boolean(userConfigration?.cardPayment) === true) {
      await processCardPayment({
        amount: Math.round(Number(receivedAmount)),
        description: `Order payment for ${cartProducts[0]?.customerId || "Unknown Customer"}`,
        onSuccess: async (paymentDetails) => {
          const paysucess = {
            ...order,
            paymentDetails,
          };
          try {
            const response = await postOrder(paysucess);
            const orderId = response.data.Id;
            if (orderId && cartId) {
              await deleteShoppingCart(cartId);
              setPrvDelete(orderId);
              setPaymentSuccess(true);
            } else if (!orderId && cartId) {
              await deleteShoppingCart(cartId);
              notifyWarning(response.data?.Message || "Customer credit limit exceeded. The order is stored as a draft order. Please review the order and adjust the payment or credit limit before proceeding.");
              setOverCreditLimit(true);
            }
          } catch (error: any) {
            const errorResponse = error.response?.data;
            if (errorResponse.draftOrderId && cartId) {
              notifyWarning(errorResponse.Message || "Customer credit limit exceeded. The order is stored as a draft order. Please review the order and adjust the payment or credit limit before proceeding.");
              await deleteShoppingCart(cartId);
              setOverCreditLimit(true);
              const approvalContactsFilter = errorResponse.approvalContacts.map((contact: any) => ({
                name: contact.name,
                email: contact.email,
                role: contact.role[0],
              }));
              setApprovalContacts(approvalContactsFilter);
              return;
            }
            errorHandling(error);
            setButtonLoading(false);
          }

        },
      });
      return;
    }

    try {
      const response = await postOrder(order);
      const orderId = response.data.Id;
      if (orderId && cartId) {
        await deleteShoppingCart(cartId);
        setPrvDelete(orderId);
        setPaymentSuccess(true);
      }
    } catch (error: any) {
      const errorResponse = error.response?.data;
      if (errorResponse.draftOrderId && cartId) {
        notifyWarning(errorResponse.Message || "Customer credit limit exceeded. The order is stored as a draft order. Please review the order and adjust the payment or credit limit before proceeding.");
        // await deleteShoppingCart(cartId);
        setOverCreditLimit(true);
        console.log(errorResponse.approvalContacts)

        if (errorResponse.approvalContacts) {
          const approvalContactsFilter = errorResponse.approvalContacts.map((contact: any) => ({
            name: contact.name,
            email: contact.email,
            role: contact.roles[0],
          }));
          setApprovalContacts(approvalContactsFilter);
        }
        setDraftOrderId(errorResponse.draftOrderId);
        const response = await getOrderDraft(errorResponse.draftOrderId)
        setDraft(response?.result ?? response);



        return;
      }
      errorHandling(error);
      setButtonLoading(false);
    }
  };

  const requestApproval = async (selectedContacts: { name: string; email: string; role: string }[]) => {
    if (!draftOrderId) {
      notifyWarning("No draft order available for approval request.");
      return;
    }

    try {
      const response = await postCreditApprovalRequest(draftOrderId, selectedContacts.map(contact => (contact.email)));
      if (response && response.success) {
        notifySuccess(response.data.Message);
        setFinishProcess(response.data.draftOrderId)
      }
      console.log(response)
    } catch (error) {
      errorHandling(error);
    }
  }

  const IncreaseCreditLimit = (newLimit: number) => {
    console.log("object//////////")
    if (!draft?.customerId) {
      notifyError("Customer details are not available.");
      return;
    }

    setLoading(true);

    putLedgerByCustomer(draft.customerId, {
      creditLimit: {
        defaultLimit: newLimit,
      }, draftOrderId: draftOrderId,
      date: localDate,
      time: now.toTimeString().split(" ")[0],
    })
      .then((res) => {
        notifySuccess("Credit limit updated successfully.");
        setPaymentSuccess(true);
        return res
      })
      .catch((error) => {
        notifyError(error || "Failed to update credit limit.");
      })
      .finally(() => {
        setLoading(false);
      });

  }

  const draftStatusUpdate = (data: string) => {
    setLoading(true)
    if (!draftOrderId) { notifyError("Try again"); return }
    const sendData = {
      approvalStatus: data,
      date: localDate,
      time: now.toTimeString().split(" ")[0],
    }
    putOrderDraft(draftOrderId, sendData)
      .then((res) => {
        notifySuccess("Credit limit updated successfully.");
        setPaymentSuccess(true);
        return res
      })
      .catch((error) => {
        notifyError(error || "Failed to update credit limit.");
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return {
    cartProducts,
    customer,
    cartLoading,
    // customerLoading,
    backToPayment,
    setBackToPayment,
    paymentSuccess,
    orderId,
    receivedAmount,
    setReceivedAmount,
    showBalance,
    setShowBalance,
    roundAmount,
    currentTime,
    handleOrderSuccess,
    prvDelete,
    buttonLoading,
    overCreditLimit,
    approvalContacts,
    requestApproval,
    finishProcess,
    IncreaseCreditLimit,
    loading,
    draftStatusUpdate
  };
};
